import { Reference, UrlReference } from '@approvalmax/types';
import { openSplitViewState, useLayout } from '@approvalmax/ui/src/components';
import { useSetAtom } from 'jotai';
import { selectors, ui } from 'modules/common';
import { useSelector } from 'modules/react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { api } from 'services/api';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { closeDocumentPreview, showDocumentPreview } from '../../actions';
import ColumnKind from '../../config/standard/columnKind';
import { PreviewMode } from '../../reducers/page/documentPreviewReducer';
import { getActiveReport, getActiveReportConfig, getDocumentPreview } from '../../selectors/pageSelectors';
import { messages } from './DocumentPreview.messages';

export const useDocumentPreview = () => {
    const setLayout = useLayout();
    const dispatch = useDispatch();
    const setSplitViewOpenState = useSetAtom(openSplitViewState);

    const company = useCompanyBasedOnUrl();

    const data = useSelector(getDocumentPreview)!;

    const report = useSelector(getActiveReport);
    const reportConfig = useSelector(getActiveReportConfig)!;

    const row = useMemo(() => report.rows.find((row) => row.id === data.requestId)!, [data.requestId, report.rows]);
    const columns = useMemo(() => reportConfig.columns.filter((column) => column.visible), [reportConfig.columns]);

    const displayName = useMemo(() => {
        const nameColumnIndex = columns.findIndex((column) => column.kind === ColumnKind.Name);

        if (nameColumnIndex !== -1) {
            // standalone organisation can be without a name that why we add "?." instead just "."
            return (row.cells[nameColumnIndex] as Reference)?.text;
        }

        return '';
    }, [columns, row.cells]);

    const attachments = useMemo(() => {
        let documents: ui.Document[] = [];

        const attachmentsColumnIndex = columns.findIndex((column) => column.kind === ColumnKind.Attachment);

        if (attachmentsColumnIndex !== -1 && row.cells[attachmentsColumnIndex]) {
            documents = row.cells[attachmentsColumnIndex].map((attachment: UrlReference) => {
                let url = api.requests.urls.getAttachment({ attachmentId: attachment.id, companyId: company.id });

                // Hack to hide PDF name in PDF-viewer if the Title meta is empty.
                url = url.replace('getAttachment?', 'getAttachment/?');

                return {
                    id: attachment.id,
                    name: attachment.text,
                    url,
                    fileType: selectors.request.getDocumentFileType(attachment.text),
                };
            });
        }

        return documents;
    }, [columns, company.id, row.cells]);

    const request = useMemo(
        () => ({
            id: data.requestId,
            displayName,
            attachments,
        }),
        [attachments, data.requestId, displayName]
    );

    const handleGoToDocument = useCallback(
        (diff: number) => {
            const documents = request.attachments;
            const pageIndex = documents.findIndex((document) => document.id === data.attachmentId);

            if (documents.length < 2) {
                return;
            }

            const maxPageIndex = documents.length - 1;

            let newPageIndex = pageIndex + diff;

            if (newPageIndex < 0) {
                newPageIndex = 0;
            }

            if (newPageIndex > maxPageIndex) {
                newPageIndex = maxPageIndex;
            }

            if (pageIndex === newPageIndex) {
                return;
            }

            const newDocumentId = documents[newPageIndex].id;

            setLayout({ hasHeader: false, hasDrawer: false });
            dispatch(
                showDocumentPreview({
                    requestId: request.id,
                    previewMode: data.previewMode,
                    attachmentId: newDocumentId,
                })
            );
        },
        [data.attachmentId, dispatch, data.previewMode, request.attachments, request.id, setLayout]
    );

    const handleGoToNextDocument = useCallback(() => {
        handleGoToDocument(1);
        setSplitViewOpenState(true);
    }, [handleGoToDocument, setSplitViewOpenState]);

    const handleGoToPrevDocument = useCallback(() => {
        handleGoToDocument(-1);
        setSplitViewOpenState(true);
    }, [handleGoToDocument, setSplitViewOpenState]);

    const handleClose = useCallback(() => {
        setLayout({ hasHeader: true, hasDrawer: true });
        dispatch(closeDocumentPreview());
        setSplitViewOpenState(false);
    }, [dispatch, setLayout, setSplitViewOpenState]);

    const activeDocument = useMemo((): ui.Document => {
        if (data.previewMode === PreviewMode.Attachment) {
            const document = request.attachments.find((attachment) => attachment.id === data.attachmentId)!;

            let url = document.url;

            if (document.fileType === selectors.types.FileType.Pdf) {
                url = document.url + '&inline=TRUE';
            }

            return { ...document, url };
        } else {
            return {
                id: request.id,
                name: request.displayName
                    ? messages.auditReportPreviewTitle({ requestName: request.displayName })
                    : messages.auditReportPreviewTitleNoName,
                fileType: selectors.types.FileType.Pdf,
                url: api.requests.urls.getAuditReport(request.id, undefined, company.id) + '&inline=TRUE',
            };
        }
    }, [data.attachmentId, company.id, data.previewMode, request.attachments, request.displayName, request.id]);

    useEffect(() => {
        setSplitViewOpenState(true);
    }, [setSplitViewOpenState]);

    const isPageable = data.previewMode === PreviewMode.Attachment && request.attachments.length > 0;
    const currentPage = useMemo(
        () => (isPageable ? request.attachments.findIndex((attachment) => attachment.id === data.attachmentId) : 0),
        [data.attachmentId, isPageable, request.attachments]
    );

    return {
        request,
        currentPage,
        activeDocument,
        handleGoToNextDocument,
        handleGoToPrevDocument,
        handleClose,
    };
};
